/**
 * Scroll Service
 * Provides utilities for smooth scrolling
 */

/**
 * Smoothly scrolls to the specified element
 * @param {string} id - The ID of the element to scroll to
 * @param {Object} options - Scroll options
 * @param {number} options.offset - Offset from the top in pixels (default: 0)
 * @param {string} options.behavior - Scroll behavior (default: 'smooth')
 * @returns {boolean} - True if element was found and scrolled to, false otherwise
 */
export const scrollToElement = (id, options = {}) => {
  const { offset = 0, behavior = "smooth" } = options;

  // Get the element
  const element = document.getElementById(id);

  if (!element) {
    console.warn(
      `Element with id "${id}" not found when attempting to scroll.`
    );
    return false;
  }

  // Calculate position
  const elementPosition = element.getBoundingClientRect().top;
  const offsetPosition = elementPosition + window.pageYOffset - offset;

  // Perform scroll
  try {
    window.scrollTo({
      top: offsetPosition,
      behavior,
    });
    return true;
  } catch (e) {
    console.warn("Smooth scrolling not supported, using fallback", e);
    window.scrollTo(0, offsetPosition);
    return true;
  }
};

/**
 * Force scroll to an element even if the standard browser behavior isn't working
 * This is a more reliable method for problematic browsers or complex layouts
 * @param {string} id - The ID of the element to scroll to
 * @param {Object} options - Scroll options
 * @param {number} options.offset - Offset from the top in pixels (default: 0)
 * @param {string} options.behavior - Scroll behavior (default: 'smooth')
 * @param {boolean} options.debug - Whether to output debug information
 * @returns {boolean} - True if element was found and scrolled to, false otherwise
 */
export const forceScrollToElement = (id, options = {}) => {
  const { offset = 0, behavior = "smooth", debug = false } = options;

  // Get the element
  const element = document.getElementById(id);

  if (!element) {
    console.warn(
      `Element with id "${id}" not found when attempting to force scroll.`
    );
    if (debug) {
      // Log all available section IDs to help debugging
      const allIds = [
        "home",
        "courses",
        "teachers",
        "topstudents",
        "donate",
        "contact",
      ];
      console.log(
        "Available section IDs:",
        allIds.map((id) => ({
          id,
          exists: !!document.getElementById(id),
        }))
      );
    }
    return false;
  }

  if (debug) {
    console.log(`Scrolling to element with id "${id}"`, {
      element,
      boundingRect: element.getBoundingClientRect(),
    });
  }

  // Calculate absolute position
  const elementPosition = element.getBoundingClientRect().top;
  const offsetPosition = elementPosition + window.pageYOffset - offset;

  // Try the standard method first
  try {
    window.scrollTo({
      top: offsetPosition,
      behavior,
    });
  } catch (e) {
    // Fallback for browsers that don't support smooth scrolling
    window.scrollTo(0, offsetPosition);
  }

  // Double-check if scroll worked, if not use scrollIntoView as backup
  setTimeout(() => {
    const newPosition = element.getBoundingClientRect().top;
    if (Math.abs(newPosition - offset) > 10) {
      if (debug) {
        console.log("Initial scroll incomplete, using scrollIntoView fallback");
      }
      try {
        element.scrollIntoView({
          behavior,
          block: "start",
        });
        // Additional adjustment for the offset
        setTimeout(() => window.scrollBy(0, -offset), 10);
      } catch (e) {
        // Ultimate fallback
        window.scrollTo(0, offsetPosition);
      }
    }
  }, 100);

  return true;
};

/**
 * Sets up anchor link handlers for a set of links
 * @param {string} selector - CSS selector for the links
 * @param {Object} options - Scroll options
 */
export const setupSmoothScrolling = (
  selector = 'a[href^="#"]',
  options = {}
) => {
  const links = document.querySelectorAll(selector);
  // Default offset for fixed navbar
  const mergedOptions = { offset: 80, ...options };
  links.forEach((link) => {
    link.addEventListener("click", (e) => {
      // Get the href attribute
      const href = link.getAttribute("href");
      
      // Check if it's a hash link from another page (e.g., /#contact)
      if (href.startsWith('/#')) {
        // If we're already on the homepage, prevent default and scroll
        if (window.location.pathname === '/') {
          e.preventDefault();
          const targetId = href.substring(2); // Remove the /# part
          if (targetId) {
            const result = forceScrollToElement(targetId, mergedOptions);
            if (!result && mergedOptions.debug) {
              console.warn(`Failed to scroll to ${targetId} from link`, link);
            }
          }
        }
        // If we're not on the homepage, let the default behavior happen
        // which will navigate to the homepage and then the hash will be processed
      } 
      // Regular hash link on the same page
      else if (href.startsWith('#')) {
        e.preventDefault();
        const targetId = href.substring(1);
        if (targetId) {
          const result = forceScrollToElement(targetId, mergedOptions);
          if (!result && mergedOptions.debug) {
            console.warn(`Failed to scroll to ${targetId} from link`, link);
          }
        }
      }
    });
  });
};

/**
 * Check if all navigation targets exist in the DOM
 * Useful for debugging when scrolling isn't working
 * @param {Array<string>} sectionIds - Array of section IDs to check
 * @returns {Object} - Object with results of the check
 */
export const checkNavigationTargets = (
  sectionIds = [
    "home",
    "courses",
    "teachers",
    "topstudents",
    "donate",
    "contact",
  ]
) => {
  const results = {};

  sectionIds.forEach((id) => {
    const element = document.getElementById(id);
    results[id] = {
      exists: !!element,
      position: element ? element.getBoundingClientRect() : null,
    };
  });

  console.log("Navigation target check results:", results);
  return results;
};

/**
 * Initialize smooth scrolling for the entire site
 * Call this from the main App component
 * @param {number} offset - Default offset to use for all scrolling
 * @param {boolean} debug - Whether to output debug information
 */
export const initSmoothScrolling = (offset = 0, debug = false) => {
  // Run on DOMContentLoaded or immediately if already loaded
  const runSetup = () => {
    setupSmoothScrolling('a[href^="#"]', { offset, debug });

    // Check if all navigation targets exist
    if (debug) {
      setTimeout(() => {
        checkNavigationTargets();
      }, 1000);
    }

    // Check for hash in URL on page load
    if (window.location.hash) {
      // Extract id from hash and scroll to it (with slight delay to ensure DOM is ready)
      setTimeout(() => {
        const id = window.location.hash.substring(1);
        forceScrollToElement(id, { offset, debug });
      }, 300);
    }
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", runSetup);
  } else {
    runSetup();
  }
};
